import { Link } from "react-router-dom";

import type { InboxEntry } from "../../../api/types";
import { ConfidenceWord } from "../../../ui/ConfidenceWord";
import { Pill } from "../../../ui/Pill";
import { SpeechMark } from "../../../ui/SpeechMark";
import { KIND_OF_WAIT_WORDS, elementKindWord } from "../shared/words";

/** "Shown first because X hangs on it", whichever way the server worded it. */
export function shownFirstSentence(text: string): string {
  const trimmed = text.trim();
  return /^shown first because/i.test(trimmed) ? trimmed : `Shown first because ${trimmed}`;
}

/** "Waiting for X", whichever way the server worded it. */
export function waitingForSentence(text: string): string {
  const trimmed = text.trim();
  return /^waiting for/i.test(trimmed) ? trimmed : `Waiting for ${trimmed}`;
}

export function cardPath(elementId: string): string {
  return `/inbox/${encodeURIComponent(elementId)}`;
}

interface InboxEntryRowProps {
  entry: InboxEntry;
  /** The agent pointed at this one. */
  pointed?: boolean;
}

/** One element that waits for the SME. Colour is for state only: the wait is orange, the rest are plain marks. */
export function InboxEntryRow({ entry, pointed = false }: InboxEntryRowProps) {
  const { element } = entry;
  return (
    <li className="ra-entry" data-pointed={pointed ? "true" : undefined} data-unsure={entry.unsure ? "true" : undefined} data-element={element.id}>
      <div className="ra-entry__main">
        <Link className="ra-entry__name" to={cardPath(element.id)} state={{ shown: true }}>
          {element.name}
        </Link>
        <code className="ra-entry__code">{element.code}</code>
        <span className="muted">{elementKindWord(element.kind)}</span>
        {element.origin === "loaded" && <Pill tone="loaded">Loaded</Pill>}
      </div>
      <div className="ra-entry__marks row">
        <Pill tone="waiting">{KIND_OF_WAIT_WORDS[entry.kind_of_wait]}</Pill>
        {entry.unsure && <ConfidenceWord confidence="Unsure" />}
        {entry.blocked && <Pill title={entry.blocked_reasons.join("; ") || undefined}>Blocked</Pill>}
        {entry.your_own_work && <Pill>Your own work</Pill>}
        {entry.notes_waiting > 0 && <SpeechMark count={entry.notes_waiting} label={`${entry.notes_waiting === 1 ? "1 note" : `${entry.notes_waiting} notes`} waiting`} />}
      </div>
      {entry.shown_first_because && <p className="ra-entry__line muted">{shownFirstSentence(entry.shown_first_because)}</p>}
      {entry.waiting_for && <p className="ra-entry__line muted">{waitingForSentence(entry.waiting_for)}</p>}
      {entry.blocked && entry.blocked_reasons.length > 0 && <p className="ra-entry__line muted">{`Cannot be validated yet: ${entry.blocked_reasons.map((reason) => reason.trim().replace(/[.\s]+$/, "")).join("; ")}.`}</p>}
    </li>
  );
}
